/**
 * HeroStrip — top-of-dashboard at-a-glance category counts.
 *
 * Four tiles in a single row on desktop (1280–1920 primary), collapsing to
 * 2-up on tablets and 1-up on phones. Counts come straight from the existing
 * `FilingsData` aggregate (`/company/:id/get_current_filings/`): one tile per
 * category (unstarted, in-progress, in-review, submitted). Every numeric value
 * is `tabular-nums` so rows stay aligned when the strip wraps.
 *
 * Note: an earlier revision of this strip surfaced "Upcoming deadlines",
 * "Est. fees outstanding", and a compliance snapshot. Those were rolled back
 * per product call — the deadline and fee fields aren't reliably populated by
 * the aggregate serializer today, and an estimated dollar figure next to an
 * unpaid invoice erodes trust on a compliance tool. We keep the semantic
 * accent system so the "Action needed" tile still pulses when it should, but
 * the *meaning* of each tile matches the pre-redesign Dashboard 1:1.
 */

import React from "react";
import {
  AlertTriangle,
  ClipboardList,
  CircleDashed,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

import { Card, CardDescription, CardTitle } from "@/components/shared/ui/card";
import { cn } from "@/lib/utils";
import { AnimatedNumber } from "./AnimatedNumber";

type TileAccent = "neutral" | "warning" | "info" | "success";

interface HeroTileProps {
  label: string;
  /** Numeric count to show in the tile. Tweens on change. */
  value: number;
  sublabel?: string;
  icon: LucideIcon;
  accent?: TileAccent;
}

const ACCENT_CLS: Record<TileAccent, string> = {
  neutral: "bg-muted text-muted-foreground",
  warning: "bg-warning/15 [color:hsl(var(--warning))]",
  info: "bg-info/10 [color:hsl(var(--info))]",
  success: "bg-success/10 [color:hsl(var(--success))]",
};

const HeroTile: React.FC<HeroTileProps> = ({
  label,
  value,
  sublabel,
  icon: Icon,
  accent = "neutral",
}) => (
  <Card density="comfortable" className="gap-4">
    <div className="flex items-start justify-between">
      <CardDescription className="text-[13px] font-medium uppercase tracking-wide">
        {label}
      </CardDescription>
      <span
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-md",
          ACCENT_CLS[accent],
        )}
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" />
      </span>
    </div>
    <div>
      <CardTitle
        className="text-[28px] font-semibold tabular-nums leading-none"
        aria-live="polite"
      >
        <AnimatedNumber value={value} />
      </CardTitle>
      {sublabel && (
        <p className="mt-2 text-sm text-muted-foreground tabular-nums">
          {sublabel}
        </p>
      )}
    </div>
  </Card>
);

export interface CategoryCounts {
  notStarted: number;
  inProgress: number;
  inReview: number;
  submitted: number;
}

interface HeroStripProps {
  counts: CategoryCounts;
  /**
   * Count of filings where `filingContext.status.blockedBy === viewerRole`.
   * When provided, the "Action needed" tile reflects this persona-filtered
   * count rather than the broad inProgress aggregate — so owner-blocked
   * and CC-blocked work no longer conflate (Designer B §2.1).
   */
  actionableForYouCount?: number;
}

const plural = (n: number, one: string, many: string): string =>
  `${n === 1 ? one : many}`;

export const HeroStrip: React.FC<HeroStripProps> = ({
  counts,
  actionableForYouCount,
}) => {
  const { notStarted, inProgress, inReview, submitted } = counts;

  // Prefer the persona-filtered count when the caller supplied it; fall
  // back to the broad inProgress aggregate for older call sites.
  const actionValue =
    typeof actionableForYouCount === "number"
      ? actionableForYouCount
      : inProgress;
  const actionSublabel =
    typeof actionableForYouCount === "number"
      ? `${plural(actionValue, "filing", "filings")} waiting on you`
      : `${plural(actionValue, "filing", "filings")} in progress`;

  return (
    <section
      aria-label="Filings by status"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <HeroTile
        label="Not started"
        value={notStarted}
        sublabel={`${plural(notStarted, "filing", "filings")} pending kickoff`}
        icon={CircleDashed}
        accent={notStarted > 0 ? "info" : "neutral"}
      />
      <HeroTile
        label="Action needed"
        value={actionValue}
        sublabel={actionSublabel}
        icon={AlertTriangle}
        accent={actionValue > 0 ? "warning" : "neutral"}
      />
      <HeroTile
        label="In review"
        value={inReview}
        sublabel={`${plural(inReview, "filing", "filings")} with RDP / staff`}
        icon={ClipboardList}
        accent="neutral"
      />
      <HeroTile
        label="Submitted"
        value={submitted}
        sublabel={`${plural(submitted, "filing", "filings")} sent to DOB`}
        icon={ShieldCheck}
        accent={submitted > 0 ? "success" : "neutral"}
      />
    </section>
  );
};

export default HeroStrip;
